import { LowerCasePipe } from '@angular/common';
import type { Signal } from '@angular/core';
import { Component, computed, input } from '@angular/core';

import { MomentModule } from 'ngx-moment';

import type { CurrentUser } from '@recruitee/account-types';
import { ActivityItemComponent } from '@recruitee/activities-and-notifications';
import type { TrackingActivity } from '@recruitee/activities-types';
import type { FullCandidate } from '@recruitee/candidate-types';
import { SourcesType } from '@recruitee/candidate-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { CandidateSourcesContainerComponent } from '@recruitee/tags-and-sources';
import {
  CollapsibleCardComponent,
  DividerComponent,
  HeadingComponent,
  RtTooltip,
  SourceNamePipe,
  TimeAgoPipe,
} from '@recruitee/user-interface';

import { CandidateFieldRowSimpleComponent } from '../candidate-field-row-simple/candidate-field-row-simple.component';
import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';

type ApplyLanguage = {
  hasLanguages: boolean;
  desc: string;
  tooltip: string | null;
};

@Component({
  selector: 'rt-candidate-details-card',
  imports: [
    CollapsibleCardComponent,
    HeadingComponent,
    TranslatePipe,
    CandidateFieldRowSimpleComponent,
    LowerCasePipe,
    TimeAgoPipe,
    MomentModule,
    RtTooltip,
    DividerComponent,
    SourceNamePipe,
    CandidateSourcesContainerComponent,
    ActivityItemComponent,
    CollapsibleCardAnalyticsDirective,
  ],
  templateUrl: './candidate-details-card.component.html',
  styleUrl: './candidate-details-card.component.less',
})
export class CandidateDetailsCardComponent {
  public readonly CandidateSource = SourcesType;

  public candidate = input.required<FullCandidate>();
  public currentAdmin = input.required<CurrentUser>();
  public lastActivity = input<TrackingActivity | null>(null);
  public isMultilangEnabled = input<boolean>(false);
  public canManageCandidates = input<boolean>(false);
  public applyLanguage: Signal<ApplyLanguage | null> = computed(() => {
    if (!this.isMultilangEnabled()) return null;

    const placementLanguages = Array.from(
      new Set(
        this.candidate()
          .placements!.filter(p => p.language)
          .map(p => p.language.name),
      ),
    );

    return {
      hasLanguages: !!placementLanguages.length,
      desc:
        placementLanguages.length > 1
          ? this.i18n.translate('app.candidate.languages.multi', {
              language: placementLanguages[0],
              languagesCount: placementLanguages.length - 1,
            })
          : this.i18n.translate('app.candidate.languages.single', {
              language: placementLanguages[0],
            }),
      tooltip: placementLanguages.length > 1 ? placementLanguages.join(', ') : null,
    };
  });

  public candidateReferrers = computed(() => {
    const referrers = Array.from(
      new Set(
        this.candidate().referralReferrers.map(referrer =>
          !referrer || referrer.anonymizedAt
            ? this.i18n.translate('app.candidate.referred.deleted_referrer')
            : `${referrer.firstName} ${referrer.lastName}`,
        ),
      ),
    );

    const [firstReferrer, ...restReferrers] = referrers;

    return {
      hasReferrers: !!firstReferrer,
      desc:
        restReferrers.length > 0
          ? this.i18n.translate('app.candidate.referred.multiple', {
              firstReferrer,
              referrersCount: restReferrers.length,
            })
          : this.i18n.translate('app.candidate.referred.single', {
              firstReferrer,
            }),
      tooltip: restReferrers.length > 0 ? restReferrers.join(', ') : null,
    };
  });

  constructor(private i18n: I18nService) {}
}
